import { keepPreviousData } from "@tanstack/react-query";
import { MRT_TableInstance } from "material-react-table";
import { useMemo, useState } from "react";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { useSysCfgCombinedQuery } from "@/api/hooks/sys-cfg-hooks";
import ConfigsEditTableControl from "@/components/configs-edit-table-control";
import { mapAccountBulkEditInputs } from "@/lib/accounts-utils";
import {
  ACCOUNT_RELATION_CONFIGS_NAMES,
  ACCOUNT_RELATION_SERVICE_CONFIGS_NAMES,
  ROOM_NAMES,
  STALE_TIME_60,
} from "@/lib/constants";
import { KeyFilter, SysAccountRegularDto, SysCfgRawDto } from "@/shared/api";

const EditAccountsTableControl = ({
  title,
  roomName,
  table,
  filters,
  onSuccess,
  totalElementsCount,
}: {
  title?: string;
  roomName: string;
  table: MRT_TableInstance<SysAccountRegularDto>;
  filters: Set<KeyFilter>;
  onSuccess?: (rowsCount: number) => void;
  totalElementsCount: number;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { data: relConfigs } = useSysCfgCombinedQuery(
    {
      domain: roomName,
      config_names: [...ACCOUNT_RELATION_CONFIGS_NAMES],
    },
    { enabled: isOpen, placeholderData: keepPreviousData, staleTime: STALE_TIME_60 }
  );

  const { data: relServiceConfigsRs } = useSysCfgCombinedQuery(
    {
      domain: ROOM_NAMES.SERVICES,
      config_names: ACCOUNT_RELATION_SERVICE_CONFIGS_NAMES,
    },
    {
      enabled: isOpen,
    }
  );

  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: isOpen,
  });

  const authGroups = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs]);

  const fields = useMemo(() => {
    const configsMap = relConfigs?.data
      .concat(relServiceConfigsRs?.data ?? [])
      .reduce(
        (acc, current) => acc.set(current.config_name, current.configs),
        new Map<string, SysCfgRawDto[]>()
      );
    return mapAccountBulkEditInputs(configsMap);
  }, [relConfigs, relServiceConfigsRs]);

  const handleOnSuccess = (count: number) => {
    setIsOpen(false);
    onSuccess && onSuccess(count);
  };

  return (
    <ConfigsEditTableControl
      title={title}
      roomName={roomName}
      table={table}
      filters={filters}
      isOpen={isOpen}
      onSuccess={handleOnSuccess}
      setIsOpen={(val) => setIsOpen(val)}
      fields={fields}
      totalElementsCount={totalElementsCount}
    />
  );
};

export default EditAccountsTableControl;
